import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme, useMediaQuery, keyframes, alpha } from "@mui/material";
import { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useMutation } from "@tanstack/react-query";
import Chat from "./components/Chat";
import PastSessions from "./components/PastSessions";
import { createSession, getSessionDetails, listTemplates, TemplateListItem, ClarificationField } from "./utils/backend-utils";
import AppContext from "./AppContext";
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import TuneIcon from '@mui/icons-material/Tune';
import QuizIcon from '@mui/icons-material/Quiz';
import Button from "@mui/material/Button";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

function Home() {
  const theme = useTheme();
  const navigate = useNavigate();
  const auth = useAuth();
  const appContext = useContext(AppContext);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [animationStarted, setAnimationStarted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TemplateListItem[] | null>(null);
  // `auth` (from react-oidc-context) is a fresh object on renders unrelated
  // to actual auth state, so depending on it directly in the fetch effect
  // below could refetch /templates far more often than intended (compounding
  // API Gateway usage-plan throttling). Home only ever mounts once already
  // authenticated (see ProtectedRoute), so we only need `auth` at fetch time.
  const authRef = useRef(auth);
  authRef.current = auth;

  // Store navigation data for after animation
  const pendingNavigationRef = useRef<{
    goal: string;
    sessionId: string;
    plan: any;
    status: string;
    message?: string;
    mode?: 'guided' | 'interrogation';
    clarification_spec?: ClarificationField[];
  } | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationStarted(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const resp = await listTemplates(authRef.current);
        if (!cancelled) setTemplates(resp.templates || []);
      } catch (e) {
        // Keep UI usable even if templates endpoint isn't available yet.
        if (!cancelled) setTemplates([]);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);
  
  // Reset landing mode when component mounts (only if coming from another route)
  useEffect(() => {
    if (appContext && !appContext.isLandingMode) {
      appContext.setIsLandingMode(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to start the transition animation and navigate
  const startTransitionAndNavigate = (
    goal: string,
    sessionId: string,
    plan: any,
    status: string,
    message?: string,
    mode?: 'guided' | 'interrogation',
    clarification_spec?: ClarificationField[],
  ) => {
    pendingNavigationRef.current = { goal, sessionId, plan, status, message, mode, clarification_spec };

    setIsTransitioning(true);

    if (appContext) {
      appContext.setIsLandingMode(false);
    }

    setTimeout(() => {
      if (pendingNavigationRef.current) {
        const { goal, sessionId, plan, status, message, mode, clarification_spec } = pendingNavigationRef.current;
        navigate("/prompt", {
          state: { goal, sessionId, plan, status, message, mode, clarification_spec },
        });
      }
    }, 500); // Match animation duration
  };

  // Mutation for creating a session
  const createSessionMutation = useMutation({
    mutationFn: (params: { goal: string; template_id?: string; mode?: 'guided' | 'interrogation' }) =>
        createSession(params.goal, auth, params.template_id, params.mode),
    onSuccess: (
      data: { session_id: string; plan: any; status: string; message?: string; mode?: 'guided' | 'interrogation'; clarification_spec?: ClarificationField[] },
      variables: { goal: string; template_id?: string; mode?: 'guided' | 'interrogation' }
    ) => {
      startTransitionAndNavigate(
        variables.goal, data.session_id, data.plan, data.status, data.message,
        data.mode || variables.mode, data.clarification_spec,
      );
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to create session. Please try again.");
      setIsSubmitting(false);
      setIsTransitioning(false);
    },
  });

  const handleSubmit = (message: string, files?: File[]) => {
    setError(null);
    setIsSubmitting(true);
    createSessionMutation.mutate({ goal: message });
  };

  const handleTemplateSubmit = (template_id: string, goal: string) => {
    setError(null);
    setIsSubmitting(true);
    createSessionMutation.mutate({ goal, template_id });
  };

  const handleInterrogationSubmit = () => {
    const goal = "Understand my work context and workflows";
    setError(null);
    setIsSubmitting(true);
    createSessionMutation.mutate({ goal, mode: 'interrogation' });
  };

  const handleSessionSelect = async (sessionId: string, goal: string) => {
    setError(null);
    setIsSubmitting(true);
    setIsTransitioning(true);
    
    try {
      // Fetch full session details
      const sessionDetails = await getSessionDetails(sessionId, auth);
      
      console.log('[SESSION RESTORE] Full session details:', JSON.stringify(sessionDetails, null, 2));
      
      // Reveal the navbar
      if (appContext) {
        appContext.setIsLandingMode(false);
      }
      
      // Navigate to the session after animation
      setTimeout(() => {
        navigate("/prompt", {
          state: {
            goal: sessionDetails.goal || goal,
            sessionId: sessionDetails.session_id,
            plan: sessionDetails.plan || [],
            status: sessionDetails.status,
            mode: sessionDetails.mode,
            messages: sessionDetails.messages || [],
            clarification_spec: sessionDetails.clarification_spec,
            isRestoredSession: true,
            execution_started_at: sessionDetails.execution_started_at,
          },
        });
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load session");
      setIsSubmitting(false);
      setIsTransitioning(false);
    }
  };

  // Define responsive sizes
  const iconSize = isSmallScreen ? "60px" : "100px";
  const logoSize = isSmallScreen ? "35px" : "50px";
  const titleVariant = isSmallScreen ? "h5" : "h3";
  const subtitleVariant = isSmallScreen ? "body1" : "h6";
  const footerTextVariant = isSmallScreen ? "caption" : "body1";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateRows: "1fr auto",
        height: "100%",
        width: "100%",
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.light, 0.03)} 0%, 
          ${alpha(theme.palette.background.paper, 1)} 50%, 
          ${alpha(theme.palette.secondary.light, 0.03)} 100%)`,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(${alpha(theme.palette.primary.main, 0.03)} 1px, transparent 1px)`,
          backgroundSize: "30px 30px",
          pointerEvents: "none",
        },
      }}
    >
      {/* Main Content Area */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          p: { xs: 2, sm: 3 },
          gap: { xs: 3, sm: 4 },
        }}
      >
        {/* Hero Section - fades in immediately, fades out on submit */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            opacity: isTransitioning ? 0 : (animationStarted ? 1 : 0),
            animation: animationStarted && !isTransitioning
              ? `${fadeIn} 0.5s ease-out forwards`
              : "none",
            transition: isTransitioning ? "opacity 0.4s ease" : "none",
          }}
        >
          <Stack
            direction={isSmallScreen ? "column" : "row"}
            alignItems="center"
            spacing={isSmallScreen ? 1.5 : 3}
            sx={{ textAlign: isSmallScreen ? "center" : "left" }}
          >
            <Box
              component="img"
              src="/icon.png"
              alt="Quantum Interrogator Icon"
              sx={{
                height: iconSize,
                width: iconSize,
                filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.1))",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05) rotate(-3deg)",
                },
              }}
            />
            <Stack spacing={0.5}>
              <Typography
                variant={titleVariant}
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  letterSpacing: "-0.02em",
                }}
              >
                Quantum Interrogator
              </Typography>
              <Typography
                variant={subtitleVariant}
                component="h2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                  opacity: 0.85,
                }}
              >
                Build and Use your own Knowledge Base
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* Chat Box - fades in 500ms after hero */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "520px",
            px: { xs: 1, sm: 2 },
            opacity: isTransitioning ? 0 : (animationStarted ? 1 : 0),
            animation: animationStarted && !isTransitioning
              ? `${fadeIn} 0.5s ease-out 0.5s forwards`
              : "none",
            animationFillMode: "both",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            transition: isTransitioning ? "opacity 0.4s ease" : "none",
            pointerEvents: isTransitioning ? "none" : "auto",
          }}
        >
          {/* Loading state */}
          {isSubmitting && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                p: 2,
                borderRadius: "12px",
                background: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Creating your session...
              </Typography>
            </Box>
          )}

          {/* Error message */}
          {error && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.error.light, 0.1),
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
              }}
            >
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          )}

          <Chat
            placeholder="What would you like to investigate today?"
            onSubmit={handleSubmit}
            navigateToChat={false}
            autoFocus={true}
            rows={4}
            maxWidth="100%"
            animationDelay={0}
          />
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              color: alpha(theme.palette.text.secondary, 0.6),
              opacity: isTransitioning ? 0 : (animationStarted ? 1 : 0),
              animation: animationStarted && !isTransitioning
                ? `${fadeIn} 0.5s ease-out 0.5s forwards`
                : "none",
              animationFillMode: "both",
              transition: isTransitioning ? "opacity 0.4s ease" : "none",
            }}
          >
            Press Enter for new line • Cmd/Ctrl + Enter to send
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 3, width: '100%', justifyContent: 'center', flexWrap: 'wrap' }}>
            {(templates && templates.length > 0) ? (
              templates.map((t) => (
                <Button
                  key={t.template_id}
                  variant="outlined"
                  startIcon={t.template_id.includes('tuning') ? <TuneIcon /> : <DeveloperBoardIcon />}
                  onClick={() => handleTemplateSubmit(t.template_id, t.goal_hint || t.title)}
                  sx={{ borderRadius: '12px', textTransform: 'none', bgcolor: 'background.paper' }}
                >
                  {t.title}
                </Button>
              ))
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<DeveloperBoardIcon />}
                  onClick={() => handleTemplateSubmit('tableau_install', 'Tableau Server Upgrade/Fresh Installation')}
                  sx={{ borderRadius: '12px', textTransform: 'none', bgcolor: 'background.paper' }}
                >
                  Tableau Install/Upgrade
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TuneIcon />}
                  onClick={() => handleTemplateSubmit('tableau_tuning', 'Tableau Server Tuning')}
                  sx={{ borderRadius: '12px', textTransform: 'none', bgcolor: 'background.paper' }}
                >
                  Tableau Tuning
                </Button>
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<QuizIcon />}
              onClick={handleInterrogationSubmit}
              sx={{ borderRadius: '12px', textTransform: 'none', mt: 1 }}
            >
              Start Expert Interrogation
            </Button>
          </Box>

          {/* Past Sessions */}
          <Box
            sx={{
              width: "100%",
              opacity: isTransitioning ? 0 : (animationStarted ? 1 : 0),
              animation: animationStarted && !isTransitioning
                ? `${fadeIn} 0.5s ease-out 0.7s forwards`
                : "none",
              animationFillMode: "both",
              transition: isTransitioning ? "opacity 0.4s ease" : "none",
            }}
          >
            <PastSessions onSessionSelect={handleSessionSelect} />
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: theme.spacing(1, 2),
          alignItems: "center",
          opacity: isTransitioning ? 0 : 1,
          transition: "opacity 0.3s ease",
        }}
      >
        <Typography
          variant={footerTextVariant}
          color="text.secondary"
          sx={{ mr: 1 }}
        >
          Powered by
        </Typography>
        <a
          href="https://quantumanalytics.ch/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Box
            component="img"
            src="/q-logo.png"
            alt="Quantum Analytics Logo"
            sx={{
              height: logoSize,
              verticalAlign: "middle",
              transition: "transform 0.2s ease, opacity 0.2s ease",
              opacity: 0.8,
              "&:hover": {
                transform: "scale(1.05)",
                opacity: 1,
              },
            }}
          />
        </a>
      </Box>
    </Box>
  );
}

export default Home;
